import axios from 'axios';
import { isTokenExpired } from '@/utils/auth';
import { apiConfig } from '@/utils/config';
import { encryptData, decryptData } from '@/utils/crypto';
import signalRService from '@/services/signalRService';
import router from '@/router';
import jwtService from '@/services/jwtService';
import { getFingerprint } from '@/services/fingerprint';
let timer = null; // Timer variable to store the token expiration timer
const state = {
    user: getUserFromLocalStorage(),
    showHeader: true,
};

function getUserFromLocalStorage() {
    try {
        return JSON.parse(decryptData(localStorage.getItem('user'))) || null
    }
    catch {
        return null;
    }
}

const mutations = {
    SET_USER(state, user) {
        state.user = user;
    },
    SET_SHOW_HEADER(state, value) {
        state.showHeader = Boolean(value) // Ensure boolean
    }
};

const actions = {

    async login({ commit, dispatch }, credentials) {
        try {
            //await dispatch('showSpinner', null, { root: true });
            const deviceId = await getFingerprint();
            const response = await axios.post(
                `${apiConfig.baseUrl}auth/login`,
                credentials,
                {
                    headers: {
                        'X-FP-EM': deviceId,
                    }
                }
            );
            if (response.data.success) {
                const token = response.data.accessToken;

                if (!response.data.hasNextAction) {
                    // Decode JWT token to get user properties
                    const decodedToken = jwtService.parseJwt(token);
                    commit('SET_USER', decodedToken);
                    localStorage.setItem('token', encryptData(token));
                    localStorage.setItem('rtoken', encryptData(response.data.refreshToken));
                    // Start token expiration timer
                    dispatch('startTokenExpirationTimer', decodedToken.exp);
                    await dispatch('hideSpinner', null, { root: true });
                    signalRService.initializeConnection();
                }
                return response.data;
            }
            else {
                throw { error: { code: '500', message: response.data.message } }
            }
        } catch (error) {
            throw error.response ? error.response.data : error;
        } finally {
            //await dispatch('hideSpinner', null, { root: true });
        }
    },

    async submitAccessCode({ commit, dispatch }, credentials) {
        try {
            await dispatch('showSpinner', null, { root: true });

            const response = await axios.post(`${apiConfig.baseUrl}user/submit-access-code`, credentials);
            if (response.data.success) {
                const user = jwtService.parseJwt(response.data.token);
                commit('SET_USER', user);
                localStorage.setItem('token', encryptData(JSON.stringify(user)));

                // Start token expiration timer
                dispatch('startTokenExpirationTimer', user.expiresIn);
                await dispatch('hideSpinner', null, { root: true });
                await signalRService.initializeConnection();
                return user;
            }
            else {
                throw { error: { code: '500', message: response.data.message } }
            }
        } catch (error) {
            throw error.response ? error.response.data : error;
        } finally {
            await dispatch('hideSpinner', null, { root: true });
        }
    },
    showAdminHeader({ commit }, value) {
        commit('SET_SHOW_HEADER', value)
    },
    setShowHeader({ commit }, value) {
        commit('SET_SHOW_HEADER', value)
    },
    async activate({ commit, dispatch }, code) {
        try {
            const response = await axios.get(`${apiConfig.baseUrl}v1/u/activate/${code}`);
            if (response.data.success) {
                const user = response.data.user;
                commit('SET_USER', user);
                localStorage.setItem('user', encryptData(JSON.stringify(user)));

                // Start token expiration timer
                dispatch('startTokenExpirationTimer', user.expiresIn);
                return user;
            }
            else {
                throw { error: { code: '', message: response.data.message } }
            }
        } catch (error) {
            throw error.response ? error.response.data : error;
        }
    },
    async logout({ commit }) {
        commit('SET_SHOW_HEADER', false);
        commit('SET_USER', null);

        if (signalRService.connection) {
            try {
                await signalRService.connection.stop();
                console.log('SignalR disconnected');
            } catch (err) {
                console.error('SignalR disconnection error:', err);
            }
        }
        Object.keys(localStorage).forEach(key => {
            if (key === 'token' || key === 'rtoken') {
                localStorage.removeItem(key);
            }
        });
        clearInterval(timer);
        timer = null;
        setTimeout(() => {
            router.push('/login');
        }, 200);
    },
    async initializeStore({ commit, dispatch }) {
        const encryptedToken = localStorage.getItem('token');
        if (!encryptedToken) {
            dispatch('logout');
            return;
        }

        try {
            const token = decryptData(encryptedToken);
            const user = jwtService.parseJwt(token);

            if (!user || isTokenExpired(user.exp)) {
                // Token has expired or invalid, attempt to refresh token
                try {
                    const refreshedUser = await dispatch('refreshToken');
                    if (refreshedUser) {
                        commit('SET_USER', refreshedUser);
                        dispatch('startTokenExpirationTimer', refreshedUser.exp);
                        signalRService.initializeConnection();
                    } else {
                        dispatch('logout');
                    }
                } catch (error) {
                    console.error('Failed to refresh token:', error);
                    dispatch('logout');
                }
            } else {
                // Token is still valid, set user and start expiration timer
                commit('SET_USER', user);
                dispatch('startTokenExpirationTimer', user.exp);
                signalRService.initializeConnection();
            }
        } catch (error) {
            console.error('Error initializing store:', error);
            dispatch('logout');
        }
    },
    startTokenExpirationTimer({ dispatch }, expiresIn) {
        // Clear existing timer if it exists
        if (timer) {
            clearTimeout(timer);
            timer = null;
        }

        // Calculate token expiration time
        const expirationTime = expiresIn * 1000; // Convert to milliseconds
        const now = Date.now();
        const timeUntilExpiration = expirationTime - now;

        // Set timer to refresh token 5 minutes before expiration
        const refreshTime = timeUntilExpiration - (5 * 60 * 1000); // 5 minutes before expiration

        if (refreshTime > 0) {
            timer = setTimeout(async () => {
                console.log('Token refresh initiated...');
                try {
                    const refreshedUser = await dispatch('refreshToken');
                    if (refreshedUser) {
                        //  console.log('Token refreshed successfully!');
                        dispatch('startTokenExpirationTimer', jwtService.parseJwt(refreshedUser.token).exp)
                    } else {
                        dispatch('logout');
                    }
                } catch (error) {
                    //console.error('Failed to refresh token:', error);
                    dispatch('logout');
                }
            }, refreshTime);
        }
        else {
            //console.log('Token expires soon, refreshing immediately...');
            const token = jwtService.parseJwt(decryptData(localStorage.getItem('token')));
            dispatch('startTokenExpirationTimer', token.exp)
        }
        /*else {
            // Token expires in less than 5 minutes, refresh immediately
            console.log('Token expires soon, refreshing immediately...');
            dispatch('refreshToken').catch(error => {
                console.error('Failed to refresh token:', error);
                dispatch('logout');
            });
        }*/
    },

    async refreshToken({ commit, dispatch }) {
        const rtoken = decryptData(localStorage.getItem("rtoken"));
        if (!rtoken) {
            console.warn("No valid refresh token available.");
            return null;
        }

        try {
            // Make a GET request to refresh the token
            const fp = await getFingerprint();
            const response = await axios.get(
                `${apiConfig.baseUrl}user/refreshtoken`,
                {
                    headers: {
                        "X-FP-EM": fp,
                        Authorization: `Bearer ${rtoken}`
                    }
                }
            );

            if (response.data.success && response.data.token) {
                // Parse the new token
                const refreshedUser = jwtService.parseJwt(response.data.token);
                if (!refreshedUser) {
                    throw new Error('Invalid token received');
                }

                // Update state and storage
                commit('SET_USER', refreshedUser);
                localStorage.setItem('token', encryptData(response.data.token));

                if (response.data.refreshToken) {
                    localStorage.setItem('rtoken', encryptData(response.data.refreshToken));
                }
                return refreshedUser;
            } else {
                throw new Error('Token refresh failed: ' + (response.data.message || 'Unknown error'));
            }
        } catch (error) {
            console.error("Failed to refresh token:", error);

            // Clear invalid tokens
            localStorage.removeItem('token');
            localStorage.removeItem('rtoken');

            if (error.response && error.response.status === 401) {
                console.warn("Refresh token expired or invalid");
                dispatch('logout')
            }

            return null;
        }
    },

    async setUser({ commit }, user) {
        try {
            commit('SET_USER', user);
        } catch (error) {
            console.error('Error setting user:', error);
        }
    },
};

const getters = {
    isAuthenticated: state => !!state.user && state.user.exp && !isTokenExpired(state.user.exp),
    currentUser: state => state.user,
    userRoles: state => state.user ? state.user.roles : [],
    showHeader: (state) => {
        // Ensure we always return a boolean
        return Boolean(state.showHeader)
    }
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters
};